import React from "react";
import {
  GanttFilter,
  OptimizedGantt,
  project,
  resource as res,
  ProjectElementType,
} from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveOptimizedGantt,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { Flag } from "@common/infiniteScroll";
import { useToggle } from "@common/treeToggle";
import { Context as CTX } from "../context";
import { ContextData, Props } from "./types";

const Context = React.createContext<ContextData>({
  project,
  template: [],
});

function useProjectCtx() {
  return React.useContext(Context);
}

function ProjectCtx({
  project,
  workLoad,
  filter,
  sort,
  isFlagRendered = true,
  children,
}: Props) {
  const { isOpen } = useToggle(`${workLoad.uuid}:${project.uuid}`, 0, false);
  const nextFilter: GanttFilter = {
    ...filter,
    scope: "project",
    projects: [project],
    resources: [{ ...res, uuid: workLoad.uuid }],
  };
  const { planzone } = useRetrieveCurrentPlanzone();
  const { setProjectElementsNbr } = React.useContext(CTX);
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project ? project.uuid : "",
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const {
    isRequestPending,
    gantt,
    currentPage,
    canRetrieveNextElements,
    retrieveOptimizedGantt,
  } = useRetrieveOptimizedGantt(nextFilter, sort, 25);
  const projectAsProjectElement: Extract<
    OptimizedGantt[number],
    { type: ProjectElementType.PROJECT }
  > = {
    uuid: project.uuid,
    type: ProjectElementType.PROJECT,
    activityRootUuid: project.rootList.uuid,
    parentUuid: "",
    parentUuids: [],
    name: project.name,
    description: project.description,
    startDate: project.rootList.startDate,
    endDate: project.rootList.endDate,
    completionDate: "",
    percentComplete: 0,
    depth: 0,
    keyRank: 0,
    referenceKeyRank: 0,
    tags: project.tags,
    attributes: project.attributes,
    zone: project.zone,
  };

  React.useEffect(() => {
    setProjectElementsNbr(project.uuid, gantt.length || 1);
  }, [gantt.length]);

  return (
    <Context.Provider value={{ project, template }}>
      {isOpen ? (
        <>
          {children(
            gantt.length === 0
              ? [projectAsProjectElement]
              : gantt.length === 0
                ? [projectAsProjectElement]
                : gantt.length > 0 &&
                    gantt[0].type === ProjectElementType.PROJECT
                  ? [
                      {
                        ...(gantt[0] as Extract<
                          OptimizedGantt[number],
                          { type: ProjectElementType.PROJECT }
                        >),
                        zone: project.zone,
                      },
                      ...gantt.slice(1),
                    ]
                  : gantt,
            isRequestPending,
          )}
          {isFlagRendered && (
            <Flag
              name={project.uuid}
              isDisabled={!canRetrieveNextElements || isRequestPending}
              retrieveItems={() =>
                retrieveOptimizedGantt({
                  filter: nextFilter,
                  sort,
                  size: 25,
                  page: currentPage,
                })
              }
            />
          )}
        </>
      ) : (
        children([projectAsProjectElement], false)
      )}
    </Context.Provider>
  );
}

export { ProjectCtx, useProjectCtx };
